/* Getting a real workbook into a register.
 *
 * The temptation is to hand the whole spreadsheet to a model and ask for rows
 * back. That is slow, it costs money in proportion to the file, and it gives a
 * different answer every time, which means nobody can check it.
 *
 * So the model never sees the data. It sees the column names and a handful of
 * sample values, and it answers with a mapping: this column is the action,
 * that one is the owner, this sheet is Action Items and the third sheet is a
 * dropdown list so skip it. One small cheap call. The app then applies that
 * mapping to all 389 rows itself, here, deterministically, and shows the
 * result before a single row is saved.
 *
 * Everything in this file is the app's half of that: what a register can be
 * told, what a sheet looks like to the mapper, and how a mapping is applied.
 */

import { REGISTERS } from "../registers.js";

/* Registers a workbook can land in. Trackers, schedules and the map layers
   have their own importers already and are not offered here. */
export const IMPORTABLE = [
  "actions", "decisions", "risks", "issues", "deliverables", "tasks",
  "milestones", "compliance", "permits", "rfis", "resources", "invoices",
  "expenses", "vendors", "procurement",
];

/* What the mapper is aiming at, taken from the register definitions rather
   than written out again here. One source of truth: a field added to a
   register becomes a field an import can fill on the same day. */
export function describeTargets(contract, keys = IMPORTABLE) {
  return keys
    .filter((key) => REGISTERS[key])
    .map((key) => {
      const register = REGISTERS[key];
      const fields = register.fields(contract) ?? [];
      return {
        register: key,
        label: register.label,
        fields: fields.map((f) => ({
          field: f.key,
          label: f.label,
          type: f.type === "select" ? "text" : f.type,
          required: Boolean(f.required),
          ...(f.options?.length ? { suggested: f.options } : {}),
        })),
      };
    });
}

/* What a sheet looks like to the mapper: its name, its columns, and enough
   real values to tell a date column from a name column. Five is enough. It is
   also deliberately few, because these values go to a third party and a
   sample is not a database. */
export function profileForMapping(sheet, sampleCount = 5) {
  if (!sheet?.usable) {
    return { name: sheet?.name ?? "", usable: false, reason: sheet?.reason ?? "" };
  }
  const samples = (col) => {
    const seen = [];
    for (const row of sheet.rows) {
      const v = row[col.key];
      if (v === null || v === undefined || String(v).trim() === "") continue;
      /* Trimmed, because the samples are here to help recognise a column and
         "Open " next to "Open" would waste a slot arguing about a space. The
         raw value is what gets applied later; this is only the description. */
      const shown = String(v).trim().slice(0, 60);
      if (!seen.includes(shown)) seen.push(shown);
      if (seen.length >= sampleCount) break;
    }
    return seen;
  };
  return {
    name: sheet.name,
    usable: true,
    rowCount: sheet.rows.length,
    columns: sheet.columns.map((c) => ({
      key: c.key, label: c.label, type: c.type, samples: samples(c),
    })),
  };
}

/* ------------------------------------------------------------ normalising */

const text = (v) => {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v).replace(/\s+/g, " ").trim();
};

/* Words that live in a date column and are not dates.
 *
 * The VIDE action log has 109 target dates reading TBD, 13 reading Ongoing,
 * and one each of Overdue, Monitoring, Weekly and "Week of 5/18/26". A date
 * column is a promise the spreadsheet does not keep, so these are recognised
 * and set aside rather than parsed into nonsense or dropped in silence. */
const NOT_A_DATE = /^(tbd|n\/?a|none|ongoing|overdue|monitoring|pending|weekly|monthly|continuous|as needed|complete[d]?|open|closed)$/i;

export function toIsoDate(value) {
  if (value === null || value === undefined) return null;
  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    const p = (n) => String(n).padStart(2, "0");
    return `${value.getFullYear()}-${p(value.getMonth() + 1)}-${p(value.getDate())}`;
  }
  const s = text(value);
  if (!s || NOT_A_DATE.test(s)) return null;

  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (iso) return `${iso[1]}-${String(+iso[2]).padStart(2, "0")}-${String(+iso[3]).padStart(2, "0")}`;

  /* US order, which is what every one of these workbooks uses. A two digit
     year is this century: these are program files from the 2020s, not
     archives, and "5/18/26" is 2026. */
  const us = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/.exec(s);
  if (us) {
    const year = us[3].length === 2 ? `20${us[3]}` : us[3];
    const month = +us[1];
    const day = +us[2];
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  }
  return null;
}

export function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const s = text(value).replace(/[$,\s]/g, "").replace(/%$/, "");
  if (!s) return null;
  /* Accounting parentheses mean negative, and every finance tab here uses
     them. Read as 1234 the sign is simply wrong. */
  const negative = /^\((.*)\)$/.exec(s);
  const n = Number(negative ? negative[1] : s);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

/* A value that should be one of a known set. Matched loosely, because "Open "
   with a trailing space and "open" are the same status to a person and two
   different ones to a computer. If nothing matches, the original is kept: a
   register must never silently rewrite a word somebody typed. */
export function toOption(value, options = []) {
  const s = text(value);
  if (!s) return "";
  const squash = (v) => v.toLowerCase().replace(/[^a-z0-9]/g, "");
  const hit = options.find((o) => squash(o) === squash(s));
  return hit ?? s;
}

/* ------------------------------------------------------------- the mapping */

/* Apply one sheet's mapping to every row in it.
 *
 * Returns the rows to save and, separately, what could not be read. The second
 * half matters as much as the first: an import that quietly drops 109 dates is
 * worse than one that refuses, because nobody finds out for a month.
 */
export function applyMapping(sheet, mapping, { contract, register } = {}) {
  const key = register || mapping?.register;
  const definition = REGISTERS[key];
  if (!definition) return { rows: [], issues: [{ kind: "register", detail: `Unknown register: ${key}` }] };

  const fields = definition.fields(contract) ?? [];
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const columns = mapping?.columns ?? {};
  const constants = mapping?.constants ?? {};

  const unread = new Map();
  const note = (field, value) => {
    const list = unread.get(field) ?? [];
    if (list.length < 6 && !list.includes(value)) list.push(value);
    unread.set(field, list);
  };
  const counts = new Map();
  const bump = (field) => counts.set(field, (counts.get(field) ?? 0) + 1);

  const rows = [];
  let blank = 0;

  for (const source of sheet.rows ?? []) {
    const row = {};

    for (const [field, column] of Object.entries(columns)) {
      if (!column) continue;
      const raw = source[column];
      const spec = byKey.get(field);
      const type = spec?.type === "select" ? "select" : spec?.type ?? "text";

      if (type === "date") {
        const iso = toIsoDate(raw);
        row[field] = iso;
        if (iso === null && text(raw)) { note(field, text(raw)); bump(field); }
      } else if (type === "number") {
        const n = toNumber(raw);
        row[field] = n;
        if (n === null && text(raw)) { note(field, text(raw)); bump(field); }
      } else if (type === "select") {
        row[field] = toOption(raw, spec?.options ?? []);
      } else {
        row[field] = text(raw);
      }
    }

    /* A constant is something true of the whole sheet that no column carries:
       the vendor whose tab this is, the district a sheet covers. */
    for (const [field, value] of Object.entries(constants)) {
      if (value !== undefined && value !== null && value !== "") row[field] = value;
    }

    /* Every register requires a title. A row without one is not a row, it is
       a spacer or a total, and both belong nowhere. */
    if (!text(row.title)) { blank += 1; continue; }

    /* Status is not null in the database. A blank one becomes the register's
       own first option rather than failing the whole import on row 200. */
    if (byKey.has("status") && !text(row.status)) {
      row.status = byKey.get("status")?.options?.[0] ?? "Open";
    }

    rows.push(row);
  }

  const issues = [...unread.entries()].map(([field, examples]) => ({
    kind: "unreadable",
    field,
    label: byKey.get(field)?.label ?? field,
    count: counts.get(field) ?? 0,
    examples,
  }));

  if (blank) {
    issues.push({
      kind: "blank",
      count: blank,
      detail: `${blank} row${blank === 1 ? "" : "s"} had nothing in the ${
        byKey.get("title")?.label?.toLowerCase() ?? "title"} column and were left out.`,
    });
  }

  return { rows, issues };
}
